import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AiScreeningConfigActions } from './config-state';
import { AiScreeningHttpService } from '../ai-screening-http.service';

export const AiScreeningConfigNotCreated = Symbol('AiScreeningConfigNotCreated');

@Injectable({ providedIn: 'root' })
export class ConfigEffects {
  private readonly actions = inject(Actions);
  private readonly aiScreeningHttpService = inject(AiScreeningHttpService);

  public loadConfig = createEffect(() =>
    this.actions.pipe(
      ofType(AiScreeningConfigActions.fetchOne),
      mergeMap(({ payload }) =>
        this.aiScreeningHttpService.fetchConfig(payload.offerId).pipe(
          map(data =>
            data
              ? AiScreeningConfigActions.fetchOneSuccess({
                  payload: { data, correlationId: payload.correlationId },
                })
              : AiScreeningConfigActions.fetchOneFailed({
                  payload: {
                    error: new HttpErrorResponse({ error: AiScreeningConfigNotCreated }),
                    id: payload.correlationId,
                  },
                }),
          ),
          catchError(error =>
            of(
              AiScreeningConfigActions.fetchOneFailed({
                payload: { error, id: payload.correlationId },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public updateConfig = createEffect(() =>
    this.actions.pipe(
      ofType(AiScreeningConfigActions.updateOnePessimistic),
      mergeMap(({ payload }) =>
        this.aiScreeningHttpService.updateConfig(payload.offerId, payload.data).pipe(
          map(data => AiScreeningConfigActions.updateOnePessimisticSuccess({ payload: { data } })),
          catchError(error =>
            of(
              AiScreeningConfigActions.updateOnePessimisticFailed({
                payload: { error, data: { id: payload.data.id } },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public createConfig = createEffect(() =>
    this.actions.pipe(
      ofType(AiScreeningConfigActions.createOnePessimistic),
      mergeMap(({ payload }) =>
        this.aiScreeningHttpService.createConfig(payload.offerId, payload.data).pipe(
          map(data =>
            AiScreeningConfigActions.createOnePessimisticSuccess({
              payload: { data, correlationId: payload.correlationId },
            }),
          ),
          catchError(error =>
            of(
              AiScreeningConfigActions.createOnePessimisticFailed({
                payload: { error, correlationId: payload.correlationId },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public generateCriteria = createEffect(() =>
    this.actions.pipe(
      ofType(AiScreeningConfigActions.generateCriteria),
      mergeMap(({ payload }) =>
        this.aiScreeningHttpService.generateCriteria(payload.offerId).pipe(
          map(data =>
            AiScreeningConfigActions.generateCriteriaSuccess({
              payload: { data },
            }),
          ),
          catchError(error =>
            of(
              AiScreeningConfigActions.generateCriteriaFailed({
                payload: { error },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public verifyCriteria = createEffect(() =>
    this.actions.pipe(
      ofType(AiScreeningConfigActions.verifyCriteria),
      mergeMap(({ payload }) =>
        this.aiScreeningHttpService.verifyCriteria(payload.offerId, payload.data).pipe(
          map(data =>
            AiScreeningConfigActions.verifyCriteriaSuccess({
              payload: { data },
            }),
          ),
          catchError(error =>
            of(
              AiScreeningConfigActions.verifyCriteriaFailed({
                payload: { error },
              }),
            ),
          ),
        ),
      ),
    ),
  );
}
